import { Text, TextVariants } from "@/components/ui/baseUi/text"
import type { PriceObjectEntity } from "@/modules/common/priceObject/entities/priceObjectEntity"
import { currencySymbol, formatCurrency, getCurrencySeparator } from "@/modules/currency/currency"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import type { VariantProps } from "class-variance-authority"

type ResumeCardPriceDisplayProps = {
  price: PriceObjectEntity
  wholePartFontStyle?: VariantProps<typeof TextVariants>["fontStyle"]
  decimalPartFontStyle?: VariantProps<typeof TextVariants>["fontStyle"]
  wholePartClassName?: string
  decimalPartClassName?: string
  className?: string
} & React.ComponentProps<"span">

export function ResumeCardPriceDisplay({
  price,
  wholePartFontStyle = "HM",
  decimalPartFontStyle = "HM",
  wholePartClassName,
  decimalPartClassName,
  className,
  ...props
}: ResumeCardPriceDisplayProps) {
  const language = useLanguage()

  const symbol = currencySymbol(price.currency, language)
  const currencySeparator = getCurrencySeparator(language)
  const formattedPrice = formatCurrency(price.value, price.currency, language)
  const symbolPosition = formattedPrice.indexOf(symbol)
  const [wholePart, decimalPart] = formattedPrice.replace(symbol, "").split(currencySeparator)

  return (
    <span className={cn("whitespace-nowrap", className)} {...props}>
      <Text fontStyle={wholePartFontStyle} className={wholePartClassName}>
        {symbolPosition === 0 ? `${symbol}${wholePart}` : wholePart}
      </Text>
      <Text fontStyle={decimalPartFontStyle} className={decimalPartClassName}>
        {decimalPart && decimalPart.trim() !== "00" ? `${currencySeparator}${decimalPart.trim()}` : ""}
        {symbolPosition > 0 ? `${symbol}` : ""}
      </Text>
    </span>
  )
}
